import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { cx } from './classNames';

// MUI's small outlined TextField in the app theme (MuiOutlinedInput in
// styles/msiMuiComponentOverrides.js): the control height (36px, 30px
// compact), 7px radius, dark line border, 12px ink text, 9px side padding,
// soft placeholder; hover darkens the border, focus takes the focus border
// and a 2px focus ring, disabled takes the disabled fill. The font family is
// inherited; the size is the field's own (the browser default would be
// 13.33px).
const BASE_CLASS_NAME = cx(
    'm-0 box-border h-9 w-full min-w-0 appearance-none rounded-[7px] border border-solid bg-ui-surface',
    'px-[9px] py-0 [font-family:inherit] text-[12px] leading-[1.4375] text-ui-ink',
    'placeholder:text-ui-placeholder placeholder:opacity-100 focus:outline-none',
    'transition-[border-color,box-shadow] duration-100 focus:shadow-[0_0_0_2px_var(--app-focusRing)]',
    'disabled:cursor-default disabled:border-ui-disabled-line disabled:bg-ui-disabled-fill',
    'disabled:text-ui-disabled-text [[data-compact]_&]:h-[30px]',
);
const BORDER_CLASS_NAMES = Object.freeze({
    normal: 'border-ui-line-strong hover:border-ui-hover-line focus:border-ui-focus',
    error: 'border-ui-danger hover:border-ui-danger focus:border-ui-danger',
});
// With adornments the same field is a box holding them around a bare input;
// it reacts to the input's focus and disabled state (`has-` variants, which
// also outrank `hover:`, as MUI's focused border does).
const FIELD_CLASS_NAME = cx(
    'box-border inline-flex h-9 w-full min-w-0 items-center gap-0.5 rounded-[7px] border border-solid',
    'bg-ui-surface px-[9px] text-[12px] leading-[1.4375] text-ui-ink transition-[border-color,box-shadow]',
    'duration-100 has-[:focus]:shadow-[0_0_0_2px_var(--app-focusRing)] has-[:disabled]:border-ui-disabled-line',
    'has-[:disabled]:bg-ui-disabled-fill has-[:disabled]:text-ui-disabled-text [[data-compact]_&]:h-[30px]',
);
const FIELD_BORDER_CLASS_NAMES = Object.freeze({
    normal: 'border-ui-line-strong hover:border-ui-hover-line has-[:focus]:border-ui-focus',
    error: 'border-ui-danger hover:border-ui-danger has-[:focus]:border-ui-danger',
});
const FIELD_INPUT_CLASS_NAME = cx(
    'm-0 box-border h-full w-0 min-w-0 flex-1 appearance-none border-0 bg-transparent p-0 [font:inherit]',
    'text-inherit placeholder:text-ui-placeholder placeholder:opacity-100 focus:outline-none',
    'disabled:cursor-default',
);
// MUI InputAdornment text: secondary, slightly smaller than the value.
const ADORNMENT_CLASS_NAME = 'shrink-0 whitespace-nowrap text-[0.7rem] text-content-secondary';
const ALIGN_CLASS_NAMES = Object.freeze({ left: undefined, right: 'text-right' });

/**
 * A single-line text input (a native <input>, so labels, roles and editing
 * are the browser's). It fills its container's width; `className` and
 * `style` style the field (e.g. a width). `error` shows the invalid state and
 * sets aria-invalid; `align="right"` right-aligns the text (numbers).
 * `startAdornment` / `endAdornment` are short texts shown inside the field
 * before and after the value (a currency or unit). Other props (value,
 * onChange, placeholder, disabled, inputMode, aria-label, aria-describedby,
 * onKeyDown…) go to the input.
 */
const TextInput = forwardRef(function TextInput({
    error = false,
    type = 'text',
    align = 'left',
    startAdornment = null,
    endAdornment = null,
    className,
    style,
    ...inputProps
}, ref) {
    const tone = error ? 'error' : 'normal';
    if (startAdornment == null && endAdornment == null) {
        return (
            <input
                ref={ref}
                type={type}
                aria-invalid={error || undefined}
                className={cx(BASE_CLASS_NAME, BORDER_CLASS_NAMES[tone], ALIGN_CLASS_NAMES[align], className)}
                style={style}
                {...inputProps}
            />
        );
    }
    return (
        <span className={cx(FIELD_CLASS_NAME, FIELD_BORDER_CLASS_NAMES[tone], className)} style={style}>
            {startAdornment != null && <span className={ADORNMENT_CLASS_NAME}>{startAdornment}</span>}
            <input
                ref={ref}
                type={type}
                aria-invalid={error || undefined}
                className={cx(FIELD_INPUT_CLASS_NAME, ALIGN_CLASS_NAMES[align])}
                {...inputProps}
            />
            {endAdornment != null && <span className={ADORNMENT_CLASS_NAME}>{endAdornment}</span>}
        </span>
    );
});

TextInput.propTypes = {
    error: PropTypes.bool,
    type: PropTypes.string,
    align: PropTypes.oneOf(['left', 'right']),
    startAdornment: PropTypes.node,
    endAdornment: PropTypes.node,
    className: PropTypes.string,
    style: PropTypes.object,
};

export default TextInput;
